import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

function Aml() {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const element = document.querySelector(location.hash);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  }, [location]);

  return (
    <>
      <Helmet>
        <title>AML &amp; CTF Policies | Cardplot</title>
        <meta
          name="description"
          content="Review Cardplot's Anti-Money Laundering (AML) and Counter-Terrorist Financing (CTF) policies, including our risk-based approach, governance framework, and customer due diligence standards."
        />
        <meta
          name="keywords"
          content="Cardplot AML policy, counter-terrorist financing, CTF policy, anti-money laundering, KYC, KYB, sanctions screening, compliance, financial crime prevention"
        />
        <meta property="og:title" content="AML &amp; CTF Policies | Cardplot" />
        <meta
          property="og:description"
          content="Learn how Cardplot prevents money laundering and terrorist financing through robust AML/CTF policies and compliance controls."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://cardplot.com/aml" />
        <meta property="og:image" content="https://cardplot.com/favicon.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="AML &amp; CTF Policies | Cardplot" />
        <meta
          name="twitter:description"
          content="Explore Cardplot's policies for detecting and preventing money laundering, terrorist financing, and other financial crimes."
        />
      </Helmet>
      <section className="breadcrumb__section bg-overlay">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="breadcrumb__text">
                <h3 className="breadcrumb__text__head">
                  ANTI-MONEY LAUNDERING (AML) &amp;
                  <br />
                  COUNTER-TERRORIST FINANCING (CTF)       
                  <br />
                  POLICIES
                </h3>
                <p className="breadcrumb__text__content color__blue size_pricing">
                  Cardplot Technologies Limited is committed to preventing money laundering, terrorist
                  financing, sanctions violations, and other financial crimes across all jurisdictions in
                  which it operates.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="legal__section">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="legal__section__content">
                <h4 className="legal__section__content__head">1. Policy Statement</h4>
                <p className="legal__section__content__text">
                  Cardplot Technologies Limited (&quot;Cardplot&quot; or &quot;the Company&quot;) maintains a
                  strict Anti-Money Laundering (AML), Counter-Terrorist Financing (CTF), Counter-Proliferation
                  Financing (CPF) framework, sanctions violations, and other financial crimes across all
                  jurisdictions in which it operates.
                </p>
                <p className="legal__section__content__text">
                  As a Pan-African fintech platform facilitating cross-border payments, multi-currency
                  services, virtual accounts, and digital financial services, Cardplot recognizes its obligation to:
                </p>
                <ul className="legal__section__content__text">
                  <li>Detect and prevent financial crime</li>
                  <li>Conduct risk-based customer due diligence</li>
                  <li>Monitor transactions in real time</li>
                  <li>Report suspicious activity to relevant authorities</li>
                  <li>Maintain auditable compliance records</li>
                </ul>
                <p className="legal__section__content__text">
                  This AML/CTF Policy establishes the step-by-step risk assessment methodology used to
                  determine whether a customer is categorized as Low Risk, Medium Risk, or High Risk prior
                  to being permitted to transact on the platform.
                </p>

                <h4 className="legal__section__content__head">2. Legal &amp; Regulatory Framework</h4>
                <p className="legal__section__content__text">
                  This policy is aligned with:
                </p>
                <ul className="legal__section__content__text">
                  <li>FATF Recommendations</li>
                  <li>AML/CFT regulations applicable in operational jurisdictions</li>
                  <li>Sanctions compliance obligations (including OFAC screening)</li>
                  <li>Applicable financial services and payment regulations</li>
                </ul>

                <h4 className="legal__section__content__head">3. Governance Structure</h4>
                <h5 className="legal__section__content__subhead">3.1 Compliance Oversight</h5>
                <p className="legal__section__content__text">AML oversight is managed by:</p>
                <ul className="legal__section__content__text">
                  <li>Chief Compliance Officer (CCO)</li>
                  <li>AML Compliance Team</li>
                  <li>Risk &amp; Fraud Monitoring Team</li>
                  <li>Board of Directors (ultimate oversight)</li>
                </ul>
                <h5 className="legal__section__content__subhead">3.2 Independent Review</h5>
                <ul className="legal__section__content__text">
                  <li>Annual AML policy review</li>
                  <li>Periodic independent compliance audit</li>
                  <li>Regulatory reporting where required</li>
                </ul>

                <h4 className="legal__section__content__head">4. Risk-Based Approach (RBA)</h4>
                <p className="legal__section__content__text">
                  Cardplot adopts a Risk-Based Approach (RBA), meaning that the level of due diligence
                  applied to a customer depends on their assessed risk level.
                </p>
                <p className="legal__section__content__text">
                  No customer is permitted to transact until AML risk classification is completed.
                </p>

                <h4 className="legal__section__content__head">
                  5. Step-by-Step Customer Risk Assessment Process
                </h4>
                <p className="legal__section__content__text">
                  Cardplot uses VOVE ID for AML screening and compliance verification.
                </p>

                <h5 className="legal__section__content__subhead">
                  Step 1: Customer Identification &amp; Data Collection
                </h5>
                <p className="legal__section__content__text">
                  At onboarding, Cardplot collects the following information.
                </p>
                <p className="legal__section__content__text">
                  <strong>For Individuals:</strong>
                </p>
                <ul className="legal__section__content__text">
                  <li>Full legal name</li>
                  <li>Date of birth</li>
                  <li>Nationality</li>
                  <li>Residential address</li>
                  <li>Government-issued ID</li>
                  <li>Selfie/liveness verification</li>
                  <li>Phone number and email</li>
                </ul>
                <p className="legal__section__content__text">
                  <strong>For Businesses:</strong>
                </p>
                <ul className="legal__section__content__text">
                  <li>Business registration documents</li>
                  <li>Directors and beneficial owners (UBOs)</li>
                  <li>Tax identification number</li>
                  <li>Registered address</li>
                  <li>Corporate bank details</li>
                </ul>
                <p className="legal__section__content__text">
                  No account proceeds to activation without full identity data.
                </p>

                <h5 className="legal__section__content__subhead">
                  Step 2: Identity Verification (KYC/KYB)
                </h5>
                <p className="legal__section__content__text">
                  Using VOVE ID, Cardplot performs:
                </p>
                <ul className="legal__section__content__text">
                  <li>Identity authentication</li>
                  <li>Liveness verification</li>
                  <li>Address verification</li>
                  <li>Beneficial ownership verification (for businesses)</li>
                </ul>
                <p className="legal__section__content__text">
                  If identity cannot be verified, onboarding is rejected.
                </p>

                <h5 className="legal__section__content__subhead">
                  Step 3: AML &amp; Sanctions Screening (Pre-Trade Screening)
                </h5>
                <p className="legal__section__content__text">
                  Before account activation, the following screenings are conducted via VOVE ID:
                </p>
                <ul className="legal__section__content__text">
                  <li>AML watchlists</li>
                  <li>OFAC sanctions list</li>
                  <li>Global sanctions lists</li>
                  <li>Politically Exposed Persons (PEP) database</li>
                  <li>Criminal records screening</li>
                  <li>Adverse media screening</li>
                </ul>
                <p className="legal__section__content__text">
                  <strong>Screening outcomes:</strong>
                </p>
                <ul className="legal__section__content__text">
                  <li>No Match – Proceed to Risk Scoring</li>
                  <li>Potential Match – Escalate for Manual Review</li>
                  <li>Confirmed Sanctions Match – Immediate Rejection &amp; Reporting</li>
                </ul>

                <h5 className="legal__section__content__subhead">
                  Step 4: Risk Scoring Methodology
                </h5>
                <p className="legal__section__content__text">
                  Cardplot assigns a numerical risk score based on weighted risk factors.
                </p>
                <p className="legal__section__content__text">
                  <strong>4.1 Customer Risk Factors</strong>
                </p>
                <ul className="legal__section__content__text">
                  <li>PEP status</li>
                  <li>Adverse media findings</li>
                  <li>Nationality and country of residence</li>
                  <li>High-risk jurisdiction exposure</li>
                  <li>Complex ownership structure (for businesses)</li>
                  <li>Source of funds clarity</li>
                </ul>
                <p className="legal__section__content__text">
                  <strong>4.2 Geographic Risk Factors</strong>
                </p>
                <ul className="legal__section__content__text">
                  <li>FATF high-risk jurisdictions</li>
                  <li>Countries under sanctions</li>
                  <li>High-corruption index jurisdictions</li>
                </ul>
                <p className="legal__section__content__text">
                  <strong>4.3 Product &amp; Transaction Risk Factors</strong>
                </p>
                <ul className="legal__section__content__text">
                  <li>Cross-border transaction usage</li>
                  <li>High transaction velocity</li>
                  <li>Large transaction volumes</li>
                  <li>Use of virtual accounts</li>
                  <li>Currency swapping patterns</li>
                </ul>

                <h5 className="legal__section__content__subhead">
                  Step 5: Risk Classification
                </h5>
                <p className="legal__section__content__text">
                  Based on the risk scoring, customers are classified as Low Risk, Medium Risk, or High
                  Risk.
                </p>
                <p className="legal__section__content__text">
                  <strong>Low Risk</strong>
                </p>
                <ul className="legal__section__content__text">
                  <li>No PEP match</li>
                  <li>No sanctions exposure</li>
                  <li>Standard jurisdiction</li>
                  <li>Transparent source of funds</li>
                  <li>Low expected transaction volume</li>
                </ul>
                <p className="legal__section__content__text">
                  <em>Action:</em> Standard Due Diligence (SDD). Account activated.
                </p>
                <p className="legal__section__content__text">
                  <strong>Medium Risk</strong>
                </p>
                <ul className="legal__section__content__text">
                  <li>Minor adverse media</li>
                  <li>Higher transaction projections</li>
                  <li>Cross-border exposure</li>
                  <li>Industry exposure (cash-intensive business)</li>
                </ul>
                <p className="legal__section__content__text">
                  <em>Action:</em> Enhanced monitoring applied.
                </p>
                <p className="legal__section__content__text">
                  <strong>High Risk</strong>
                </p>
                <ul className="legal__section__content__text">
                  <li>PEP status</li>
                  <li>Sanctions proximity</li>
                  <li>High-risk jurisdiction</li>
                  <li>Complex ownership structure</li>
                  <li>Significant adverse media</li>
                  <li>Suspicious onboarding indicators</li>
                </ul>
                <p className="legal__section__content__text">
                  <em>Action:</em>
                </p>
                <ul className="legal__section__content__text">
                  <li>Enhanced Due Diligence (EDD)</li>
                  <li>Source of Funds verification</li>
                  <li>Senior Compliance approval required</li>
                  <li>Ongoing heightened monitoring</li>
                </ul>

                <h4 className="legal__section__content__head">6. Enhanced Due Diligence (EDD)</h4>
                <p className="legal__section__content__text">
                  For High-Risk Customers, Cardplot requires:
                </p>
                <ul className="legal__section__content__text">
                  <li>Detailed Source of Funds documentation</li>
                  <li>Source of Wealth declaration</li>
                  <li>Additional ID verification</li>
                  <li>Manual compliance review</li>
                  <li>Senior management approval</li>
                </ul>
                <p className="legal__section__content__text">
                  Failure to satisfy EDD requirements results in account rejection.
                </p>

                <h4 className="legal__section__content__head">7. Ongoing Transaction Monitoring</h4>
                <p className="legal__section__content__text">
                  AML compliance does not end at onboarding. Cardplot maintains continuous transaction
                  monitoring.
                </p>
                <p className="legal__section__content__text">
                  <strong>7.1 Automated Monitoring</strong>
                </p>
                <ul className="legal__section__content__text">
                  <li>Real-time transaction analysis</li>
                  <li>Velocity checks</li>
                  <li>Pattern recognition algorithms</li>
                  <li>Geographic anomaly detection</li>
                  <li>Sudden balance fluctuation detection</li>
                </ul>
                <p className="legal__section__content__text">
                  <strong>7.2 Red Flag Indicators</strong>
                </p>
                <ul className="legal__section__content__text">
                  <li>Structuring (smurfing) behavior</li>
                  <li>Rapid in/out transactions</li>
                  <li>Unusual cross-border patterns</li>
                  <li>Dormant account reactivation with high value</li>
                  <li>Transactions inconsistent with customer profile</li>
                </ul>

                <h4 className="legal__section__content__head">8. Suspicious Activity Management</h4>
                <p className="legal__section__content__text">
                  If suspicious activity is detected, the following steps are taken:
                </p>
                <p className="legal__section__content__text">
                  <strong>Step 1: Flagging</strong> – Automated system or compliance analyst flags the
                  account.
                </p>
                <p className="legal__section__content__text">
                  <strong>Step 2: Internal Review</strong> – AML Team reviews transaction history and
                  screening results.
                </p>
                <p className="legal__section__content__text">
                  <strong>Step 3: Escalation</strong> – Escalate to Chief Compliance Officer if risk is
                  confirmed.
                </p>
                <p className="legal__section__content__text">
                  <strong>Step 4: Action</strong> – This may include:
                </p>
                <ul className="legal__section__content__text">
                  <li>Temporary account restriction</li>
                  <li>Transaction freeze (where permitted)</li>
                  <li>Filing Suspicious Activity Report (SAR)</li>
                  <li>Regulatory reporting (where required)</li>
                </ul>

                <h4 className="legal__section__content__head">9. Sanctions Compliance</h4>
                <p className="legal__section__content__text">
                  Cardplot maintains zero tolerance for:
                </p>
                <ul className="legal__section__content__text">
                  <li>OFAC-listed individuals/entities</li>
                  <li>UN-sanctioned individuals</li>
                  <li>Prohibited jurisdictions</li>
                </ul>
                <p className="legal__section__content__text">
                  Sanctions screening is:
                </p>
                <ul className="legal__section__content__text">
                  <li>Conducted at onboarding</li>
                  <li>Conducted periodically thereafter</li>
                  <li>Triggered upon major account updates</li>
                </ul>

                <h4 className="legal__section__content__head">
                  10. Shell Company Identification and Risk Management Policy
                </h4>
                <h5 className="legal__section__content__subhead">10.1 Policy Statement</h5>
                <p className="legal__section__content__text">
                  As part of its risk-based approach, Cardplot implements enhanced controls to identify,
                  assess, and manage risks associated with shell companies and opaque corporate structures
                  during onboarding and throughout the customer lifecycle.
                </p>
                <p className="legal__section__content__text">
                  Cardplot does not provide services to anonymous shell banks or entities that fail to
                  disclose Ultimate Beneficial Ownership (UBO). Entities lacking transparency, economic
                  substance, or legitimate commercial purpose may be declined.
                </p>

                <h5 className="legal__section__content__subhead">10.2 Definition</h5>
                <p className="legal__section__content__text">
                  For this Manual, a shell company refers to a legal entity that has no significant
                  operational presence, limited or no independent economic activity, and may be used to
                  obscure beneficial ownership or the origin of funds.
                </p>
                <p className="legal__section__content__text">
                  This includes entities that:
                </p>
                <ul className="legal__section__content__text">
                  <li>Have no verifiable physical office or employees</li>
                  <li>Operate through nominee directors or shareholders without transparency</li>
                  <li>Exhibit complex ownership layering designed to conceal natural persons</li>
                  <li>
                    Are incorporated in secrecy or high-risk jurisdictions without commercial justification
                  </li>
                </ul>
                <p className="legal__section__content__text">
                  Cardplot recognizes that certain holding companies and Special Purpose Vehicles (SPVs)
                  may be legitimate; however, such entities are subject to Enhanced Due Diligence (EDD).
                </p>

                <h5 className="legal__section__content__subhead">
                  10.3 Identification Controls at Onboarding
                </h5>
                <p className="legal__section__content__text">
                  <strong>A. Corporate Documentation Requirements</strong>
                </p>
                <p className="legal__section__content__text">
                  All corporate applicants must provide:
                </p>
                <ul className="legal__section__content__text">
                  <li>Certificate of Incorporation or Registration</li>
                  <li>Memorandum and Articles of Association (or equivalent constitutional documents)</li>
                  <li>Proof of registered and operating address</li>
                  <li>Board Resolution authorizing account opening</li>
                  <li>Identification documents for Directors</li>
                  <li>Tax identification number (where applicable)</li>
                </ul>
                <p className="legal__section__content__text">
                  All documents are independently verified through official registries and/or approved
                  third-party compliance databases.
                </p>
                <p className="legal__section__content__text">
                  <strong>B. Ultimate Beneficial Ownership (UBO) Transparency</strong>
                </p>
                <p className="legal__section__content__text">
                  Cardplot requires identification and verification of all Ultimate Beneficial Owners
                  holding 10–25% or more ownership or exercising effective control.
                </p>
                <p className="legal__section__content__text">
                  UBO verification includes:
                </p>
                <ul className="legal__section__content__text">
                  <li>Government-issued identification</li>
                  <li>Proof of residential address</li>
                  <li>Source of Wealth declaration</li>
                  <li>Sanctions screening</li>
                  <li>Politically Exposed Person (PEP) screening</li>
                  <li>Adverse media screening</li>
                </ul>
                <p className="legal__section__content__text">
                  Ownership structures are traced through all corporate layers until natural persons are
                  identified. Failure to adequately disclose UBO information results in rejection of the
                  application.
                </p>
                <p className="legal__section__content__text">
                  <strong>C. Economic Substance Assessment</strong>
                </p>
                <p className="legal__section__content__text">
                  To determine whether a corporate applicant has legitimate operational substance, Cardplot
                  may assess:
                </p>
                <ul className="legal__section__content__text">
                  <li>Business model and stated purpose</li>
                  <li>Website and digital footprint</li>
                  <li>Evidence of commercial activity (contracts, invoices, financial statements)</li>
                  <li>Operational address verification</li>
                  <li>Employee or management structure</li>
                  <li>Banking history, where required</li>
                </ul>
                <p className="legal__section__content__text">
                  Entities that demonstrate no credible operational footprint may be escalated to Enhanced
                  Due Diligence or declined.
                </p>

                <h5 className="legal__section__content__subhead">10.4 Jurisdictional Risk Assessment</h5>
                <p className="legal__section__content__text">
                  Corporate entities incorporated in:
                </p>
                <ul className="legal__section__content__text">
                  <li>FATF high-risk jurisdictions</li>
                  <li>Sanctioned territories</li>
                  <li>Offshore secrecy havens</li>
                </ul>
                <p className="legal__section__content__text">
                  Are automatically classified as high-risk and subject to Enhanced Due Diligence and senior
                  compliance approval prior to onboarding.
                </p>

                <h5 className="legal__section__content__subhead">10.5 Enhanced Due Diligence (EDD)</h5>
                <p className="legal__section__content__text">
                  Where shell company indicators or high-risk factors are identified, Cardplot shall perform
                  Enhanced Due Diligence measures, including but not limited to:
                </p>
                <ul className="legal__section__content__text">
                  <li>Independent registry confirmation of corporate status</li>
                  <li>Expanded UBO tracing beyond the first ownership layer</li>
                  <li>Source of Funds (SoF) verification</li>
                  <li>Clarification of transaction purpose and expected activity</li>
                  <li>Senior Compliance Officer review and approval</li>
                </ul>
                <p className="legal__section__content__text">
                  If risk cannot be reasonably mitigated, onboarding shall be declined.
                </p>

                <h5 className="legal__section__content__subhead">10.6 Prohibited Relationships</h5>
                <p className="legal__section__content__text">
                  Cardplot does not:
                </p>
                <ul className="legal__section__content__text">
                  <li>Onboard or maintain relationships with anonymous shell banks</li>
                  <li>Permit nested relationships through unregulated entities</li>
                  <li>Facilitate payable-through or pass-through accounts lacking transparency</li>
                  <li>Provide services where beneficial ownership cannot be verified</li>
                </ul>

                <h5 className="legal__section__content__subhead">10.7 Ongoing Monitoring</h5>
                <p className="legal__section__content__text">
                  Shell company risk assessment extends beyond onboarding. Cardplot implements:
                </p>
                <ul className="legal__section__content__text">
                  <li>Risk-based transaction monitoring</li>
                  <li>Behavioral and velocity monitoring</li>
                  <li>Comparison of declared business activity against transaction volume</li>
                  <li>Trigger-based KYC refresh</li>
                  <li>Periodic customer risk re-assessment</li>
                </ul>
                <p className="legal__section__content__text">
                  Where suspicious activity is identified, appropriate escalation procedures shall be
                  followed, including internal reporting and filing of Suspicious Activity Reports (SARs)
                  where required by law.
                </p>

                <h5 className="legal__section__content__subhead">10.8 Governance and Escalation</h5>
                <p className="legal__section__content__text">
                  All shell-company-related risk determinations must be documented.
                </p>
                <ul className="legal__section__content__text">
                  <li>High-risk corporate accounts require Compliance sign-off.</li>
                  <li>
                    Decisions to decline, restrict, or terminate relationships must be recorded with
                    justification.
                  </li>
                  <li>Senior management oversight applies to escalated cases.</li>
                </ul>

                <h4 className="legal__section__content__head">11. Record Keeping</h4>
                <p className="legal__section__content__text">
                  Cardplot maintains AML records for the legally required retention period, including:
                </p>
                <ul className="legal__section__content__text">
                  <li>KYC documentation</li>
                  <li>Screening results</li>
                  <li>Risk scoring records</li>
                  <li>Transaction logs</li>
                  <li>Suspicious activity investigations</li>
                </ul>
                <p className="legal__section__content__text">
                  Records are securely stored and auditable.
                </p>

                <h4 className="legal__section__content__head">12. Employee Training</h4>
                <p className="legal__section__content__text">
                  All relevant staff must:
                </p>
                <ul className="legal__section__content__text">
                  <li>Undergo annual AML training</li>
                  <li>Understand red flags</li>
                  <li>Report suspicious activity immediately</li>
                  <li>Avoid tipping-off customers</li>
                </ul>

                <h4 className="legal__section__content__head">13. Prohibition on Tipping-Off</h4>
                <p className="legal__section__content__text">
                  Employees must not inform customers that:
                </p>
                <ul className="legal__section__content__text">
                  <li>They are under AML investigation</li>
                  <li>A SAR has been filed</li>
                  <li>Their transactions are being reported</li>
                </ul>
                <p className="legal__section__content__text">
                  Violation constitutes serious misconduct.
                </p>

                <h4 className="legal__section__content__head">14. Policy Review</h4>
                <p className="legal__section__content__text">
                  This AML Policy is reviewed:
                </p>
                <ul className="legal__section__content__text">
                  <li>Annually</li>
                  <li>Upon regulatory changes</li>
                  <li>Upon expansion into new jurisdictions</li>
                </ul>

                <h4 className="legal__section__content__head">15. Zero Tolerance Position</h4>
                <p className="legal__section__content__text">
                  Cardplot Technologies Limited maintains zero tolerance for:
                </p>
                <ul className="legal__section__content__text">
                  <li>Money laundering</li>
                  <li>Terrorist financing</li>
                  <li>Sanctions violations</li>
                  <li>Fraud facilitation</li>
                </ul>
                <p className="legal__section__content__text">
                  Failure to comply with this policy may result in:
                </p>
                <ul className="legal__section__content__text">
                  <li>Account termination</li>
                  <li>Regulatory reporting</li>
                  <li>Legal action</li>
                </ul>

                <h4 className="legal__section__content__head">16. Updates to This Policy</h4>
                <p className="legal__section__content__text">
                  We may update this AML &amp; CTF Policy from time to time to reflect regulatory or
                  operational changes. Material changes will be communicated via:
                </p>
                <ul className="legal__section__content__text">
                  <li>Email notification</li>
                  <li>In-app notification</li>
                  <li>Website announcement</li>
                </ul>
                <p className="legal__section__content__text">
                  Last Time Updated Date: January 10th, 2026
                </p>

                <h4 className="legal__section__content__head">17. Contact Information</h4>
                <p className="legal__section__content__text">
                  For AML &amp; CTF related issues and inquiries please contact:
                  <br />
                  Cardplot Technologies Limited
                  <br />
                  Email:{' '}
                  <a>
                    legal@cardplot.com
                  </a>
                  <br />
                  Website:{' '}
                  <a href="https://cardplot.com" target="_blank" rel="noopener noreferrer">
                    https://cardplot.com
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

export default Aml;

    

         